import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import sanityClient from '../lib/sanityClient';
import { processPlayerStats } from '../utils/statsProcessing';
import { GridSkeleton } from '../components/ui/LoadingSkeleton';
import ErrorBoundary from '../components/ui/ErrorBoundary';
import logger from '../utils/logger';

function DefenseStatsPage() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const [defenseStats, setDefenseStats] = useState([]);
  const [originalPlayerData, setOriginalPlayerData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [seasons, setSeasons] = useState([]);

  useEffect(() => {
    fetchDefenseStats();

    // Auto-refresh stats every 5 minutes
    const interval = setInterval(
      () => {
        logger.info('Auto-refreshing defense stats data');
        fetchDefenseStats();
      },
      5 * 60 * 1000
    );

    return () => clearInterval(interval);
  }, [seasonId]);

  const fetchDefenseStats = async () => {
    try {
      setIsLoading(true);
      setError(null);

      logger.info('Fetching defense statistics data');

      const data = await sanityClient.fetch(`
        {
          "players": *[_type == "player" && (active == true || defined(currentSeasonStats.gp) && currentSeasonStats.gp > 0)] | order(active desc, name asc) {
            _id,
            name,
            number,
            positionGroup,
            "imageUrl": image.asset->url,
            slug,
            captainStatus,
            active,
            seasonStats[] {
              _key,
              season->{
                _id,
                name
              },
              statsType,
              gp,
              g,
              a,
              pts,
              pim,
              ppg,
              ppa,
              shg,
              otg,
              eng,
              mins
            },
            currentSeasonStats
          },
          "seasons": *[_type == "season"] | order(startDate desc) {
            _id,
            name,
            startDate,
            endDate,
            active
          }
        }
      `);

      logger.info('Successfully fetched defense stats data', {
        players: data.players?.length || 0,
        seasons: data.seasons?.length || 0,
      });

      // Filter players to only show defense
      const defense = data.players.filter(
        (player) => player.positionGroup?.toLowerCase() === 'defense'
      );

      // Process the defense data
      const processedDefense = processPlayerStats(defense, selectedSeason);

      setOriginalPlayerData(defense);
      setDefenseStats(processedDefense);
      setSeasons(data.seasons || []);

      // Set season based on URL param or default to current season
      let targetSeason;
      if (seasonId) {
        targetSeason = data.seasons?.find((s) => s._id === seasonId);
      } else {
        targetSeason = data.seasons?.find((s) => s.active) || data.seasons?.[0];
      }

      if (targetSeason) {
        setSelectedSeason(targetSeason);
        // Reprocess with the target season
        const reprocessedDefense = processPlayerStats(defense, targetSeason);
        setDefenseStats(reprocessedDefense);
      }
    } catch (err) {
      logger.error('Error fetching defense stats data', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeasonChange = (newSeasonId) => {
    if (newSeasonId) {
      navigate(`/stats/${newSeasonId}/defense`);
    } else {
      navigate('/stats/defense');
    }
  };

  const renderDefenseRow = (player) => {
    const isInactive = player.active === false;
    const rowClasses = `transition-colors ${
      isInactive
        ? 'bg-gray-800/50 hover:bg-gray-700/50 opacity-75'
        : 'bg-gray-800 hover:bg-gray-700'
    }`;

    // Get skater stats for this season
    let stats = null;
    if (player.processedStats?.statsByPosition?.skater) {
      stats = player.processedStats.statsByPosition.skater;
    } else if (player.seasonStats?.length > 0) {
      const relevantStats = selectedSeason
        ? player.seasonStats.filter((stat) => stat.season?._id === selectedSeason._id)
        : player.seasonStats;
      stats =
        relevantStats.find((stat) => stat.statsType === 'skater' || stat.statsType === 'defense') ||
        relevantStats[0];
    } else if (player.currentSeasonStats) {
      stats = player.currentSeasonStats;
    }

    if (!stats) stats = {};

    return (
      <tr key={player._id} className={rowClasses}>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center space-x-2">
            {isInactive && (
              <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                INACTIVE
              </span>
            )}
            {player.slug?.current ? (
              <Link
                to={`/players/${player.slug.current}`}
                className={`font-medium transition-colors ${
                  isInactive
                    ? 'text-gray-400 hover:text-gray-300'
                    : 'text-white hover:text-yellow-400'
                }`}
              >
                {player.name}
              </Link>
            ) : (
              <span className={`font-medium ${isInactive ? 'text-gray-400' : 'text-white'}`}>
                {player.name}
              </span>
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">#{player.number || '—'}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{player.positionGroup}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats.gp || 0}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats.g || 0}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats.a || 0}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats.pts || 0}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats.pim || 0}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats.ppg || 0}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats.ppa || 0}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats.shg || 0}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{stats.mins || 0}</td>
      </tr>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="h-8 bg-gray-700 rounded w-64 mx-auto mb-4 animate-pulse"></div>
          </div>
          <GridSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Error Loading Defense Statistics</h1>
            <p className="text-gray-400 mb-4">{error}</p>
            <button
              onClick={fetchDefenseStats}
              className="bg-yellow-600 hover:bg-yellow-700 text-black px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
              Defense Statistics
            </h1>
            {selectedSeason && (
              <p className="text-xl text-gray-300 mb-6">{selectedSeason.name} Season</p>
            )}
          </div>

          {/* Season Selector */}
          {seasons.length > 1 && (
            <div className="mb-8 flex justify-center">
              <select
                value={selectedSeason?._id || ''}
                onChange={(e) => handleSeasonChange(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="">All Seasons</option>
                {seasons.map((season) => (
                  <option key={season._id} value={season._id}>
                    {season.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Defense Table */}
          <div className="bg-gray-800 rounded-lg shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-black">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      Player
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      GP
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      G
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      A
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      PTS
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      PIM
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      PPG
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      PPA
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      SHG
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-yellow-500 uppercase tracking-wider">
                      MIN
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {defenseStats.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="px-6 py-8 text-center text-gray-400">
                        No defense stats found for this season.
                      </td>
                    </tr>
                  ) : (
                    defenseStats.map((player) => renderDefenseRow(player))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default DefenseStatsPage;
